import { useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  useTimeTrackerStore,
  ImportResult,
} from "../store/timeTrackerStore";
import { ImportFile, ImportFileSchema } from "../store/schema";
import { Button, ConfirmDialog, Dialog, useToast } from "@lazar/ui";

interface ImportPreview {
  data: ImportFile;
  mode: "skip" | "overwrite";
  result: ImportResult;
}

const DataManagement = () => {
  const { addToast } = useToast();
  const { exportTimeEntries, pruneAllTimeEntries, pruneOldTimeEntries, importData } =
    useTimeTrackerStore();

  const [confirmPrune, setConfirmPrune] = useState<"all" | "old" | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = (format: "csv" | "json") => {
    const exported = exportTimeEntries(format);
    const fileType = format === "csv" ? "text/csv" : "application/json";
    const fileName = `lemon-pi-export-${new Date()
      .toISOString()
      .slice(0, 10)}.${format}`;

    const blob = new Blob([exported], { type: fileType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    URL.revokeObjectURL(url);
    document.body.removeChild(a);
    addToast(`Exported as ${format.toUpperCase()}`, "success");
  };

  const handleFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    let text: string;
    try {
      text = await file.text();
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "import-read" } });
      addToast("Could not read that file", "error");
      return;
    }

    let raw: unknown;
    try {
      raw = JSON.parse(text);
    } catch {
      addToast("That file isn't valid JSON", "error");
      return;
    }

    const parsed = ImportFileSchema.safeParse(raw);
    if (!parsed.success) {
      Sentry.captureException(
        new Error("Import file failed schema validation"),
        { contexts: { zod: { issues: parsed.error.issues } } },
      );
      addToast(
        "This file doesn't look like a Lemon Pi JSON export",
        "error",
      );
      return;
    }

    const result = importData(parsed.data, { mode: "skip", dryRun: true });
    setPreview({ data: parsed.data, mode: "skip", result });
  };

  const recomputePreview = (mode: "skip" | "overwrite") => {
    if (!preview) return;
    const result = importData(preview.data, { mode, dryRun: true });
    setPreview({ data: preview.data, mode, result });
  };

  const applyImport = () => {
    if (!preview) return;
    const result = importData(preview.data, { mode: preview.mode });
    const summary = `Imported ${result.entries.added} ${result.entries.added === 1 ? "entry" : "entries"}, ${result.clients.added} ${result.clients.added === 1 ? "client" : "clients"}`;
    addToast(summary, "success");
    setPreview(null);
  };

  const confirmPruneAction = () => {
    if (confirmPrune === "all") {
      pruneAllTimeEntries();
      addToast("All entries pruned", "success");
    } else if (confirmPrune === "old") {
      pruneOldTimeEntries(90);
      addToast("Old entries pruned", "success");
    }
    setConfirmPrune(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-ink-soft dark:text-cream/70 mb-3">
          Export your time entries to back them up or move them to another
          device. JSON is the full-fidelity format and is the only one you can
          import back; CSV is a one-way export for spreadsheets and external
          analysis.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => handleExport("json")} variant="primary">
            Export JSON
          </Button>
          <Button onClick={() => handleExport("csv")}>Export CSV</Button>
        </div>
      </div>

      <div>
        <h5 className="text-sm font-black text-ink dark:text-cream mb-2">
          Import
        </h5>
        <p className="text-sm text-ink-soft dark:text-cream/70 mb-3">
          Restore from a Lemon Pi JSON export. You'll see exactly what will
          change before anything is applied. Existing entries with matching
          IDs are skipped by default; clients are reconciled by ID and name
          so re-importing your own data is safe.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileSelected}
          className="hidden"
          aria-hidden="true"
        />
        <Button onClick={() => fileInputRef.current?.click()}>
          Import JSON
        </Button>
      </div>

      <div>
        <h5 className="text-sm font-black text-ink dark:text-cream mb-2">
          Prune
        </h5>
        <p className="text-sm text-ink-soft dark:text-cream/70 mb-3">
          Permanently delete time entries. Use this to keep the store lean or
          start fresh. Cannot be undone.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setConfirmPrune("old")}>
            Delete older than 90 days
          </Button>
          <Button onClick={() => setConfirmPrune("all")} variant="danger">
            Delete all entries
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmPrune}
        title={
          confirmPrune === "all" ? "Delete All Entries" : "Delete Old Entries"
        }
        message={
          confirmPrune === "all"
            ? "Are you sure you want to delete ALL time entries? This cannot be undone."
            : "Are you sure you want to delete all time entries older than 90 days? This cannot be undone."
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmPruneAction}
        onCancel={() => setConfirmPrune(null)}
      />

      {preview && (
        <ImportPreviewModal
          preview={preview}
          onModeChange={recomputePreview}
          onCancel={() => setPreview(null)}
          onApply={applyImport}
        />
      )}
    </div>
  );
};

interface ImportPreviewModalProps {
  preview: ImportPreview;
  onModeChange: (mode: "skip" | "overwrite") => void;
  onCancel: () => void;
  onApply: () => void;
}

const ImportPreviewModal = ({
  preview,
  onModeChange,
  onCancel,
  onApply,
}: ImportPreviewModalProps) => {
  const { data, mode, result } = preview;
  const exportedAt = new Date(data.exportedAt).toLocaleString();
  const overwrite = mode === "overwrite";

  return (
    <Dialog
      isOpen
      onClose={onCancel}
      size="md"
      labelledBy="import-preview-title"
    >
        <h3
          id="import-preview-title"
          className="text-lg font-black mb-1 dark:text-cream"
        >
          Import preview
        </h3>
        <p className="text-xs text-ink-soft dark:text-cream/60 mb-4">
          File exported {exportedAt}
        </p>

        <div className="space-y-4 text-sm">
          <section>
            <h4 className="font-black uppercase tracking-wider text-xs text-ink dark:text-cream mb-2">
              Time entries ({data.entries.length} in file)
            </h4>
            <ul className="space-y-1 text-ink dark:text-cream">
              <li>
                <span className="font-bold">{result.entries.added}</span> new
                {result.entries.added === 1 ? " entry" : " entries"} will be
                added
              </li>
              {overwrite ? (
                <li>
                  <span className="font-bold">
                    {result.entries.overwritten}
                  </span>{" "}
                  existing
                  {result.entries.overwritten === 1 ? " entry" : " entries"}{" "}
                  will be overwritten
                </li>
              ) : (
                <li>
                  <span className="font-bold">{result.entries.skipped}</span>{" "}
                  duplicate
                  {result.entries.skipped === 1 ? " entry" : " entries"} will
                  be skipped (already in your data)
                </li>
              )}
              {result.entries.droppedRunning > 0 && (
                <li className="text-ink-soft dark:text-cream/70">
                  {result.entries.droppedRunning} running{" "}
                  {result.entries.droppedRunning === 1 ? "entry" : "entries"}{" "}
                  dropped (running state is local-only)
                </li>
              )}
              {result.entries.unresolvedClient > 0 && (
                <li className="text-ink-soft dark:text-cream/70">
                  {result.entries.unresolvedClient}{" "}
                  {result.entries.unresolvedClient === 1
                    ? "entry"
                    : "entries"}{" "}
                  will be imported without a client (client could not be
                  resolved)
                </li>
              )}
            </ul>
          </section>

          <section>
            <h4 className="font-black uppercase tracking-wider text-xs text-ink dark:text-cream mb-2">
              Clients ({data.clients.length} in file)
            </h4>
            <ul className="space-y-1 text-ink dark:text-cream">
              <li>
                <span className="font-bold">{result.clients.added}</span> new
                {result.clients.added === 1 ? " client" : " clients"} will be
                added
              </li>
              {result.clients.reused > 0 && (
                <li className="text-ink-soft dark:text-cream/70">
                  {result.clients.reused} matched existing{" "}
                  {result.clients.reused === 1 ? "client" : "clients"} and
                  will be reused
                </li>
              )}
              {overwrite && result.clients.updated > 0 && (
                <li>
                  <span className="font-bold">{result.clients.updated}</span>{" "}
                  existing
                  {result.clients.updated === 1 ? " client" : " clients"}{" "}
                  (color / target / archive state) will be updated
                </li>
              )}
              {result.clients.idConflicts > 0 && (
                <li className="text-ink-soft dark:text-cream/70">
                  {result.clients.idConflicts} imported under a fresh ID due
                  to ID conflict with a different local client
                </li>
              )}
            </ul>
          </section>

          <label className="flex items-start gap-2 cursor-pointer pt-2 border-t-2 border-ink/30 dark:border-cream/30">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) =>
                onModeChange(e.target.checked ? "overwrite" : "skip")
              }
              className="mt-0.5"
            />
            <span className="text-sm text-ink dark:text-cream">
              <span className="font-bold">Overwrite existing data</span>{" "}
              <span className="text-ink-soft dark:text-cream/70">
                — rewrite local entries and client metadata when IDs collide,
                instead of skipping them.
              </span>
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-5 mt-4 border-t-2 border-ink/30 dark:border-cream/30">
          <Button onClick={onCancel}>Cancel</Button>
          <Button onClick={onApply} variant="primary">
            Import
          </Button>
        </div>
    </Dialog>
  );
};

export default DataManagement;
